import { idbDel, idbGet, idbSet } from './indexedDbKv'

const SCHEMA_VERSION = 1
const PREFIX = `sofera:v${SCHEMA_VERSION}`
const IDB_PREFIX = `${PREFIX}:idb`
const IDLE_MS = 5000

const pending = new Map()
const timers = new Map()
let listenersBound = false

function now() {
  return Date.now()
}

function crc32(str) {
  let crc = 0 ^ -1
  for (let i = 0; i < str.length; i++) {
    let c = str.charCodeAt(i)
    crc = (crc >>> 8) ^ table[(crc ^ c) & 0xff]
  }
  return (crc ^ -1) >>> 0
}

const table = (() => {
  const t = new Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1)
    t[i] = c >>> 0
  }
  return t
})()

function hex32(n) {
  return (n >>> 0).toString(16).padStart(8, '0')
}

function pack(data) {
  const d = data ?? null
  const raw = JSON.stringify(d)
  const c = hex32(crc32(raw))
  return JSON.stringify({ v: SCHEMA_VERSION, ts: now(), c, d })
}

function tryBackupCorrupt(key, raw) {
  try {
    if (!raw) return
    const b = `${key}:corrupt:${now()}`
    localStorage.setItem(b, String(raw))
  } catch (e) { void e }
}

function unpack(key, raw, fallback) {
  if (!raw) return fallback
  try {
    const obj = JSON.parse(String(raw))
    if (!obj || typeof obj !== 'object') throw new Error('invalid')
    if (obj.v !== SCHEMA_VERSION) throw new Error('version')
    const d = obj.d
    const c = String(obj.c || '')
    const computed = hex32(crc32(JSON.stringify(d ?? null)))
    if (!c || c !== computed) throw new Error('checksum')
    return d ?? fallback
  } catch (e) {
    void e
    tryBackupCorrupt(key, raw)
    try { localStorage.removeItem(key) } catch (e2) { void e2 }
    return fallback
  }
}

function userPrefix(userId) {
  const u = userId ? String(userId) : 'anon'
  return `${PREFIX}:u:${u}`
}

function k(userId, name) {
  return `${userPrefix(userId)}:${name}`
}

function idbKey(userId, name) {
  const u = userId ? String(userId) : 'anon'
  return `${IDB_PREFIX}:u:${u}:${name}`
}

function canUseLocalStorage() {
  try {
    return typeof localStorage === 'object' && !!localStorage
  } catch {
    return false
  }
}

function bindFlushListeners() {
  if (listenersBound) return
  listenersBound = true
  if (typeof window === 'undefined') return

  const flush = () => {
    try { flushAll() } catch (e) { void e }
  }

  try {
    window.addEventListener('beforeunload', flush)
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
  } catch (e) { void e }
}

function scheduleWrite(key, writer, raw, immediate = false) {
  pending.set(key, { writer, raw })
  const prev = timers.get(key)
  if (prev) clearTimeout(prev)
  if (immediate) {
    timers.delete(key)
    const item = pending.get(key)
    if (!item) return
    pending.delete(key)
    try { item.writer(key, item.raw) } catch (e) { void e }
    bindFlushListeners()
    return
  }
  const t = setTimeout(() => {
    timers.delete(key)
    const item = pending.get(key)
    if (!item) return
    pending.delete(key)
    try { item.writer(key, item.raw) } catch (e) { void e }
  }, IDLE_MS)
  timers.set(key, t)
  bindFlushListeners()
}

export function flushAll() {
  for (const [key, t] of timers.entries()) {
    try { clearTimeout(t) } catch (e) { void e }
    timers.delete(key)
    const item = pending.get(key)
    if (!item) continue
    pending.delete(key)
    try { item.writer(key, item.raw) } catch (e) { void e }
  }
}

export function loadCompleted(userId, courseId) {
  const key = k(userId, `completed:${courseId}`)
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  return unpack(key, raw, [])
}

export function saveCompleted(userId, courseId, ids, opts) {
  const key = k(userId, `completed:${courseId}`)
  const raw = pack(Array.isArray(ids) ? ids : [])
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw, !!opts?.immediate)
}

export function loadPendingTimedCompletion(userId, courseId) {
  const key = k(userId, `pending_timed_completion:${courseId}`)
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  const data = unpack(key, raw, {})
  return data && typeof data === 'object' && !Array.isArray(data) ? data : {}
}

export function savePendingTimedCompletion(userId, courseId, pendingByLesson, opts) {
  const key = k(userId, `pending_timed_completion:${courseId}`)
  const value = pendingByLesson && typeof pendingByLesson === 'object' && !Array.isArray(pendingByLesson)
    ? pendingByLesson
    : {}
  const raw = pack(value)
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw, !!opts?.immediate)
}

export async function loadNotes(userId, courseId) {
  const key = idbKey(userId, `notes:${courseId}`)
  const raw = await idbGet(key).catch(() => null)
  if (!raw) return []
  try {
    const obj = JSON.parse(String(raw))
    if (!obj || obj.v !== SCHEMA_VERSION) throw new Error('version')
    const d = obj.d ?? []
    const c = String(obj.c || '')
    const computed = hex32(crc32(JSON.stringify(d ?? null)))
    if (!c || c !== computed) throw new Error('checksum')
    return Array.isArray(d) ? d : []
  } catch (e) {
    void e
    try { await idbDel(key) } catch (e2) { void e2 }
    return []
  }
}

export function saveNotes(userId, courseId, notes, opts) {
  const key = idbKey(userId, `notes:${courseId}`)
  const raw = pack(Array.isArray(notes) ? notes : [])
  scheduleWrite(key, async (kk, rr) => {
    await idbSet(kk, rr)
  }, raw, !!opts?.immediate)
}

export function getVideoProgressMs(userId, storageKey) {
  const key = k(userId, `video:${storageKey}`)
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  const ms = unpack(key, raw, 0)
  const n = Number(ms)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
}

export function setVideoProgressMs(userId, storageKey, ms, opts) {
  const key = k(userId, `video:${storageKey}`)
  const v = Math.max(0, Math.floor(Number(ms) || 0))
  const raw = pack(v)
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw, !!opts?.immediate)
}

export function getLessonTimeMs(userId, lessonId) {
  const key = k(userId, `lesson_time:${lessonId}`)
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  const ms = unpack(key, raw, 0)
  const n = Number(ms)
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0
}

export function setLessonTimeMs(userId, lessonId, ms, opts) {
  const key = k(userId, `lesson_time:${lessonId}`)
  const v = Math.max(0, Math.floor(Number(ms) || 0))
  const raw = pack(v)
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw, !!opts?.immediate)
}

export function clearUserPersistence(userId) {
  if (!canUseLocalStorage()) return
  const prefix = `${userPrefix(userId)}:`
  const keys = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && String(key).startsWith(prefix)) keys.push(String(key))
  }
  for (const key of keys) {
    try { localStorage.removeItem(key) } catch (e) { void e }
  }
}

export function loadCoursePatch(userId) {
  const key = k(userId, 'courses:patch')
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  const data = unpack(key, raw, { overrides: {}, added: [] })
  const overrides = (data && typeof data === 'object' && data.overrides && typeof data.overrides === 'object') ? data.overrides : {}
  const added = (data && typeof data === 'object' && Array.isArray(data.added)) ? data.added : []
  return { overrides, added }
}

export function hydrateCourses(userId, baseCourses) {
  const base = Array.isArray(baseCourses) ? baseCourses : []
  const baseById = new Map(base.map(c => [String(c?.id), c]))
  const { overrides, added } = loadCoursePatch(userId)
  const out = base.map((c) => {
    const id = String(c?.id)
    const ov = overrides ? overrides[id] : null
    let course = c
    if (ov && typeof ov === 'object') course = { ...c, ...ov }
    return { ...course, title: course.title || course.name || '' }
  })
  for (const c of added || []) {
    const id = String(c?.id)
    if (baseById.has(id)) continue
    out.push({ ...c, title: c.title || c.name || '' })
  }
  return out
}

export function saveCoursePatch(userId, currentCourses, baseCourses) {
  const base = Array.isArray(baseCourses) ? baseCourses : []
  const current = Array.isArray(currentCourses) ? currentCourses : []
  const baseById = new Map(base.map(c => [String(c?.id), c]))
  const overrides = {}
  const added = []
  for (const c of current) {
    const id = String(c?.id)
    const b = baseById.get(id)
    if (!b) {
      added.push(c)
      continue
    }
    try {
      const same = JSON.stringify(b) === JSON.stringify(c)
      if (!same) overrides[id] = c
    } catch (e) {
      void e
      overrides[id] = c
    }
  }
  const key = k(userId, 'courses:patch')
  const raw = pack({ overrides, added })
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw)
}

export function clearCoursePatch(userId, courseId) {
  const { overrides, added } = loadCoursePatch(userId)
  const id = String(courseId)
  
  if (overrides[id]) {
    delete overrides[id]
  }
  
  const newAdded = added.filter(c => String(c.id) !== id)
  
  const key = k(userId, 'courses:patch')
  const raw = pack({ overrides, added: newAdded })
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw, true)
}

export function loadAssessmentsPatch(userId) {
  const key = k(userId, 'assessments:patch')
  const raw = canUseLocalStorage() ? localStorage.getItem(key) : null
  const data = unpack(key, raw, { overrides: {}, added: [] })
  const overrides = (data && typeof data === 'object' && data.overrides && typeof data.overrides === 'object') ? data.overrides : {}
  const added = (data && typeof data === 'object' && Array.isArray(data.added)) ? data.added : []
  return { overrides, added }
}

export function hydrateAssessments(userId, baseAssessments) {
  const base = Array.isArray(baseAssessments) ? baseAssessments : []
  const baseById = new Map(base.map(a => [String(a?.id), a]))
  const { overrides, added } = loadAssessmentsPatch(userId)
  const out = base.map((a) => {
    const id = String(a?.id)
    const ov = overrides ? overrides[id] : null
    if (ov && typeof ov === 'object') return { ...a, ...ov }
    return a
  })
  for (const a of added || []) {
    const id = String(a?.id)
    if (baseById.has(id)) continue
    out.push(a)
  }
  return out
}

export function saveAssessmentsPatch(userId, currentAssessments, baseAssessments) {
  const base = Array.isArray(baseAssessments) ? baseAssessments : []
  const current = Array.isArray(currentAssessments) ? currentAssessments : []
  const baseById = new Map(base.map(a => [String(a?.id), a]))
  const overrides = {}
  const added = []
  for (const a of current) {
    const id = String(a?.id)
    const b = baseById.get(id)
    if (!b) {
      added.push(a)
      continue
    }
    try {
      const same = JSON.stringify(b) === JSON.stringify(a)
      if (!same) overrides[id] = a
    } catch (e) {
      void e
      overrides[id] = a
    }
  }
  const key = k(userId, 'assessments:patch')
  const raw = pack({ overrides, added })
  scheduleWrite(key, (kk, rr) => localStorage.setItem(kk, rr), raw)
}

export async function loadAssessmentAnswers(userId) {
  const key = idbKey(userId, 'assessmentAnswers')
  const raw = await idbGet(key).catch(() => null)
  if (!raw) return {}
  try {
    const obj = JSON.parse(String(raw))
    if (!obj || obj.v !== SCHEMA_VERSION) throw new Error('version')
    const d = obj.d ?? {}
    const c = String(obj.c || '')
    const computed = hex32(crc32(JSON.stringify(d ?? null)))
    if (!c || c !== computed) throw new Error('checksum')
    return d && typeof d === 'object' ? d : {}
  } catch (e) {
    void e
    try { await idbDel(key) } catch (e2) { void e2 }
    return {}
  }
}

export function saveAssessmentAnswers(userId, answersObj, opts) {
  const key = idbKey(userId, 'assessmentAnswers')
  const raw = pack(answersObj && typeof answersObj === 'object' ? answersObj : {})
  scheduleWrite(key, async (kk, rr) => {
    await idbSet(kk, rr)
  }, raw, !!opts?.immediate)
}
